import { jsx } from 'react/jsx-runtime';
import { Component, createRef } from 'react';
import { KEY_MAP, shiftTabPressed, tabPressed } from '@nicollet/common';
import tabbable from 'tabbable';

const focusTrap = (WrappedComponent, {
  allowArrowKeyNavigation = false
} = {}) => {
  class FocusTrap extends Component {
    constructor(props) {
      super(props);
      this.focusableElements = [];
      this.containerRef = createRef();
      this.focusToElementByIndex = this.focusToElementByIndex.bind(this);
      this.trapFocus = this.trapFocus.bind(this);
    }

    componentDidMount() {
      this.addListeners();
    }

    componentDidUpdate() {
      this.removeListeners();
      this.addListeners();
    }

    componentWillUnmount() {
      this.removeListeners();
    }

    removeListeners() {
      var _a;

      (_a = this.focusableElements) === null || _a === void 0 ? void 0 : _a.forEach(element => {
        element.removeEventListener('keydown', this.trapFocus);
      });
    }

    focusToElementByIndex(index = 0) {
      if (this.focusableElements) {
        const focusableElement = this.focusableElements[index];
        focusableElement.focus();
      }
    }

    trapFocus(e) {
      var _a;

      const {
        key,
        target
      } = e;
      const currentTarget = target;
      const lastIndex = this.focusableElements && this.focusableElements.length - 1;
      const currentIndex = currentTarget && ((_a = this.focusableElements) === null || _a === void 0 ? void 0 : _a.indexOf(currentTarget));
      const isFirstElement = currentIndex === 0;
      const isLastElement = currentIndex === lastIndex; // Check if arrow key navigation happened

      const arrowUpPressed = allowArrowKeyNavigation && key === KEY_MAP.ARROW_UP;
      const arrowDownPressed = allowArrowKeyNavigation && key === KEY_MAP.ARROW_DOWN;

      if (isFirstElement && (shiftTabPressed(e) || arrowUpPressed) && lastIndex) {
        e.preventDefault();
        return this.focusToElementByIndex(lastIndex);
      }

      if (isLastElement && (tabPressed(e) || arrowDownPressed)) {
        e.preventDefault();
        return this.focusToElementByIndex(0);
      }

      if (arrowUpPressed) {
        e.preventDefault();
        return currentIndex && this.focusToElementByIndex(currentIndex - 1);
      }

      if (arrowDownPressed) {
        e.preventDefault();
        return currentIndex !== undefined && currentIndex !== null && this.focusToElementByIndex(currentIndex + 1);
      }

      return null;
    }

    addListeners() {
      var _a;

      if (!this.containerRef || !this.containerRef.current) return;
      this.focusableElements = tabbable(this.containerRef.current);

      if (allowArrowKeyNavigation) {
        (_a = this.focusableElements) === null || _a === void 0 ? void 0 : _a.forEach(element => {
          element.addEventListener('keydown', this.trapFocus);
        });
      } else if (this.focusableElements) {
        const lastIndex = this.focusableElements.length - 1; // Only attach the listeners to the first and last element

        if (this.focusableElements[0]) {
          this.focusableElements[0].addEventListener('keydown', this.trapFocus);
        }

        if (this.focusableElements[lastIndex]) {
          this.focusableElements[lastIndex].addEventListener('keydown', this.trapFocus);
        }
      }
    }

    render() {
      return jsx("div", Object.assign({
        ref: this.containerRef
      }, {
        children: jsx(WrappedComponent, Object.assign({}, this.props), void 0)
      }), void 0);
    }

  }

  FocusTrap.displayName = 'FocusTrap';
  return FocusTrap;
};

export { focusTrap };
